import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { t } from '../i18n';
import { colors, radius, spacing, typography } from '../theme/tokens';
import type { LinkedinSubScores } from '../types';

const CLES: (keyof LinkedinSubScores)[] = ['title', 'about', 'experiences'];

export interface SousScoresLinkedinProps {
  sousScores?: LinkedinSubScores;
  testID?: string;
}

const borne = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
const lisible = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * Une note par section du profil (titre, À propos, expériences), en barres sur fond nuit, sous
 * l'anneau du score. Pensé pour le contenu de `CarteScore`.
 *
 * Une section que le serveur n'a pas notée (absente des captures, ancien serveur) n'a PAS de ligne :
 * jamais un 0 à sa place. Sans aucune note lisible, le bloc entier disparaît.
 */
export function SousScoresLinkedin({ sousScores, testID = 'li-sousscores' }: SousScoresLinkedinProps) {
  const lignes = CLES.filter((cle) => lisible(sousScores?.[cle]));
  if (lignes.length === 0) return null;
  return (
    <View testID={testID} style={styles.bloc}>
      <Text accessibilityRole="header" style={styles.titre}>
        {t('linkedin.sousScores.titre')}
      </Text>
      {lignes.map((cle) => {
        const note = borne(sousScores?.[cle] as number);
        const libelle = t(`linkedin.sousScores.${cle}`);
        return (
          <View
            key={cle}
            testID={`${testID}-${cle}`}
            accessible
            accessibilityLabel={`${libelle}, ${note} ${t('common.scoreSur100')}`}
            style={styles.ligne}
          >
            <Text style={styles.libelle} numberOfLines={2}>
              {libelle}
            </Text>
            <View accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={styles.piste}>
              <View testID={`${testID}-${cle}-barre`} style={[styles.remplissage, { width: `${note}%` }]} />
            </View>
            <Text testID={`${testID}-${cle}-note`} style={styles.note}>
              {note}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  bloc: { gap: spacing.sm },
  titre: {
    fontSize: typography.caption.size + 1,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing + 0.2,
    textTransform: 'uppercase',
    color: colors.blancAtenue,
  },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  libelle: { width: '30%', fontSize: typography.small.size, lineHeight: typography.small.lineHeight, fontWeight: '700', color: colors.white },
  piste: { flex: 1, height: 7, borderRadius: radius.pill, overflow: 'hidden', backgroundColor: 'rgba(255,255,255,0.14)' },
  remplissage: { height: '100%', borderRadius: radius.pill, backgroundColor: colors.white },
  note: { minWidth: 28, textAlign: 'center', fontSize: typography.small.size, fontWeight: '800', color: colors.white },
});
